import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text'
import Box from 'core-system/Box/Box'
import AlternateButton from 'core-system/Button/AlternateButton'
import AddIcon from 'core-system/Icons/Actions/Add'
import Input from 'core-system/Input'
import Dropdown from 'core-system/Dropdown'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import FlexContainer from 'core-system/FlexContainer'
import DatePicker from 'core-system/DatePicker'
import OnboardingTasksDrawer from 'features/Onboarding/components/OnboardingTasksDrawer'

const BoxContainer = styled(Box)`
  padding: 1rem;
  margin-bottom: 1.5rem;
`

const payFrequencyTypes = [
  {
    id: 'monthly',
    text: 'Monthly: Pay on a specific day each month',
  },
  {
    id: 'semi-monthly',
    text: 'Semi-Monthly: Two pay dates per month',
  },
  {
    id: 'bi-weekly',
    text: 'Bi-Weekly: Pay every two weeks',
  },
  {
    id: 'weekly',
    text: 'Weekly: Pay every week',
  },
]

const payCadenceDays = [
  {
    id: 'monday',
    text: 'Monday',
  },
  {
    id: 'tuesday',
    text: 'Tuesday',
  },
  {
    id: 'wednesday',
    text: 'Wednesday',
  },
  {
    id: 'thursday',
    text: 'Thursday',
  },
  {
    id: 'friday',
    text: 'Friday',
  },
  {
    id: 'saturday',
    text: 'Saturday',
  },
  {
    id: 'sunday',
    text: 'Sunday',
  },
]

const DatePickerPayCadence = ({
  placeholderText,
  selected,
  onSelect,
}: {
  placeholderText: string
  selected: Date
  onSelect: (date: Date) => void
}) => {
  return (
    <DatePicker
      placeholderText={placeholderText}
      selected={selected}
      onSelect={onSelect}
      dateFormat='d'
      openToDate={new Date(2024, 0, 1)} // Defaulting to a month with 31 days
    />
  )
}

const PayCadence = ({
  payCadence,
  removePayCadence,
}: {
  payCadence: any
  removePayCadence: () => void
}) => {
  const [payCadenceName, setPayCadenceName] = useState('')
  const [payCadenceFrequency, setPayCadenceFrequency] = useState(null)
  const [payCadenceDay, setPayCadenceDay] = useState(null)
  const [payCadenceDate, setPayCadenceDate] = useState(null)
  const [payCadenceDate2, setPayCadenceDate2] = useState(null)
  return (
    <BoxContainer overflowHidden={false}>
      <FlexContainer
        flexDirection='row'
        alignItems='center'
        marginBottom='1rem'
      >
        <Input
          placeholder='Enter Pay Cadence Name'
          value={payCadenceName}
          onChange={(e) => setPayCadenceName(e.target.value)}
          height='3.5rem'
          marginRight='1rem'
        />
        <DeleteOutlineIcon onClick={removePayCadence} />
      </FlexContainer>
      <Dropdown
        items={payFrequencyTypes}
        placeholder='Select Pay Frequency'
        active={payCadenceFrequency}
        itemCallback={({ id }) => setPayCadenceFrequency(id)}
        width='100%'
        marginBottom='1rem'
      />
      {(payCadenceFrequency === 'bi-weekly' ||
        payCadenceFrequency === 'weekly') && (
        <Dropdown
          items={payCadenceDays}
          active={payCadenceDay}
          placeholder='Select Pay Day'
          width='100%'
          itemCallback={({ id }) => setPayCadenceDay(id)}
          marginBottom='1rem'
        />
      )}
      {payCadenceFrequency === 'monthly' && (
        <DatePickerPayCadence
          placeholderText='Select Pay Date'
          selected={payCadenceDate}
          onSelect={(date) => setPayCadenceDate(date)}
        />
      )}
      {payCadenceFrequency === 'semi-monthly' && (
        <FlexContainer
          flexDirection='row'
          alignItems='center'
          justifyContent='space-between'
        >
          <DatePickerPayCadence
            placeholderText='First Pay Date'
            selected={payCadenceDate}
            onSelect={(date) => setPayCadenceDate(date)}
          />
          <DatePickerPayCadence
            placeholderText='Second Pay Date'
            selected={payCadenceDate2}
            onSelect={(date) => setPayCadenceDate2(date)}
          />
        </FlexContainer>
      )}
    </BoxContainer>
  )
}

interface PayCadenceDrawerProps {
  isOpen: boolean
  onClose: () => void
}

const PayCadenceDrawer = ({ isOpen, onClose }: PayCadenceDrawerProps) => {
  const [payCadences, setPayCadences] = useState([])

  /*
    The date picker we are using shows month and year by default.
    We are overriding the default styles to hide the header and navigation buttons for month and year.
    This is the only way to hide the month and year selectors.
  */
  useEffect(() => {
    // const style = document.createElement('style')
    // style.innerHTML = `
    //   .react-datepicker__header {
    //     display: none;
    //   }
    //   .react-datepicker__navigation--previous {
    //     display: none;
    //   }
    //   .react-datepicker__navigation--next {
    //     display: none;
    //   }
    // `
    // document.head.appendChild(style)
    // return () => {
    //   document.head.removeChild(style)
    // }
  }, [])

  const removePayCadence = (index: number) => {
    setPayCadences(payCadences.filter((_, i) => i !== index))
  }

  return (
    <OnboardingTasksDrawer
      isOpen={isOpen}
      onClose={onClose}
      primaryButton={{
        text: 'Confirm Pay Schedule',
        onClick: () => {},
      }}
      secondaryButton={{
        text: 'Save and Complete Later',
        onClick: () => {},
      }}
    >
      <BoxContainer overflowHidden={false}>
        <Text variant='h4' marginBottom='0.5rem'>
          Set Up Your Pay Schedules
        </Text>
        <Text variant='body1' marginBottom='1.5rem'>
          Customize how and when your team gets paid. You can create multiple
          pay schedules to fit different team needs.
        </Text>
        {payCadences.map((payCadence, index) => (
          <PayCadence
            key={payCadence.id}
            payCadence={payCadence}
            removePayCadence={() => removePayCadence(index)}
          />
        ))}
        <AlternateButton
          fullWidth
          startIcon={<AddIcon />}
          onClick={() =>
            setPayCadences([...payCadences, { id: payCadences.length + 1 }])
          }
        >
          Add Pay Cadence
        </AlternateButton>
      </BoxContainer>
    </OnboardingTasksDrawer>
  )
}

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  PayCadenceDrawer.displayName = 'PayCadenceDrawer'
}

export default React.memo(PayCadenceDrawer)
